import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";

const NEW_PROMO_ID = "promo_recurring_ride_reminder_farediscount_new_passenger";
const EXISTING_PROMO_ID = "promo_recurring_ride_reminder_farediscount_existing_passenger";

const GENERAL_FIELDS = [
  {
    key: "lookback_weeks",
    label: "Look-back window (weeks)",
    help: "How many weeks of completed trips the daily detector scans to find repeating rides. Longer windows catch slower habits but react slower to changes.",
  },
  {
    key: "time_bucket_minutes",
    label: "Time bucket (minutes)",
    help: "Rides at similar times are grouped together. 60 means 08:10 and 08:50 count as the same ride time. Smaller values are stricter.",
  },
  {
    key: "sender_interval_minutes",
    label: "Reminder sender interval (minutes)",
    help: "How often the sender looks for due reminders. Currently the sender job runs hourly; changing this value is stored for reference and needs a server restart plan before it changes timing.",
    readOnly: true,
  },
];

const RULE_FIELDS = [
  {
    key: "new_rider_max_trips",
    label: "New passenger: max completed trips",
    help: "A passenger with this many completed trips or fewer is treated as a new passenger. Everyone above it is an existing passenger.",
  },
  {
    key: "new_rider_min_frequency",
    label: "New passenger: matching trips needed",
    help: "How many times a new passenger must have done the same route, weekday and time before they get a reminder. 1 means a single past ride is enough.",
  },
  {
    key: "existing_rider_min_frequency",
    label: "Existing passenger: matching trips needed",
    help: "Same rule for existing passengers. Usually higher than the new passenger value so regulars are only reminded about real habits.",
  },
];

function Field({ field, value, onChange }) {
  return (
    <div className="mb-4">
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{field.label}</label>
      <input
        type="number"
        min="1"
        disabled={field.readOnly}
        value={value ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
        className={`w-full rounded-lg border p-2 ${field.readOnly ? "bg-gray-100 text-gray-500" : ""}`}
      />
      <p className="mt-1 text-xs text-gray-500">{field.help}</p>
    </div>
  );
}

function PromoCard({ title, promoId, promo, budget, description }) {
  return (
    <div className="mb-4 rounded-lg border bg-gray-50 p-4 dark:bg-gray-700">
      <h4 className="mb-1 font-semibold text-gray-800 dark:text-white">{title}</h4>
      <p className="mb-2 text-xs text-gray-500">{description}</p>
      <p className="mb-2 break-all text-xs text-gray-500">Promotion key: {promoId}</p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="block text-xs text-gray-500">Discount amount</span>
          <input disabled value={promo ? `$${Number(promo.reward_amount).toFixed(2)}` : "-"} className="w-full rounded border bg-gray-100 p-2 text-gray-500" />
        </div>
        <div>
          <span className="block text-xs text-gray-500">Status</span>
          <input disabled value={promo ? (promo.is_active ? "Active" : "Inactive") : "Not found"} className="w-full rounded border bg-gray-100 p-2 text-gray-500" />
        </div>
        <div>
          <span className="block text-xs text-gray-500">Budget remaining</span>
          <input disabled value={budget ? `$${Number(budget.remaining).toFixed(2)}` : "-"} className="w-full rounded border bg-gray-100 p-2 text-gray-500" />
        </div>
        <div>
          <span className="block text-xs text-gray-500">Budget spent</span>
          <input disabled value={budget ? `$${Number(budget.spent).toFixed(2)}` : "-"} className="w-full rounded border bg-gray-100 p-2 text-gray-500" />
        </div>
      </div>
    </div>
  );
}

export default function RoutineReminders() {
  const [settings, setSettings] = useState(null);
  const [promos, setPromos] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const s = await (await fetch(`${API_URL}/routine-reminders/settings`)).json();
      setSettings(s.data);
      const c = await (await fetch(`${API_URL}/promotions/configurations`)).json();
      setPromos(c.data || c.configurations || []);
      const b = await (await fetch(`${API_URL}/promotions/wallet/budgets`)).json();
      setBudgets(b.data || b.budgets || []);
    } catch (e) {
      setMessage(`Failed to load: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const change = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    const n = (k) => parseInt(settings[k], 10);
    if (n("new_rider_min_frequency") > n("existing_rider_min_frequency")) {
      setMessage("New passenger matching trips should not be higher than existing passenger matching trips.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        lookback_weeks: n("lookback_weeks"),
        time_bucket_minutes: n("time_bucket_minutes"),
        new_rider_max_trips: n("new_rider_max_trips"),
        new_rider_min_frequency: n("new_rider_min_frequency"),
        existing_rider_min_frequency: n("existing_rider_min_frequency"),
        new_rider_promo_config_id: NEW_PROMO_ID,
        existing_rider_promo_config_id: EXISTING_PROMO_ID,
      };
      const res = await fetch(`${API_URL}/routine-reminders/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to save");
      setSettings(data.data);
      setMessage("Saved. Changes apply from the next daily detection run.");
    } catch (e) {
      setMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  const promoFor = (id) => promos.find((p) => p.promo_id === id);
  const budgetFor = (id) => budgets.find((b) => b.promo_id === id);

  if (!settings) {
    return (
      <DashboardLayout>
        <div className="p-6">{message || "Loading..."}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-4xl p-6">
        <h2 className="mb-1 text-2xl font-bold text-gray-800 dark:text-white">Predictive Ride Reminders</h2>
        <p className="mb-6 text-sm text-gray-500">
          Finds passengers who repeat the same ride, then reminds them 24 hours and 1 hour before the predicted ride on WhatsApp
          (SMS if WhatsApp fails). Passengers can stop reminders with one tap.
        </p>

        <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">General settings</h3>
          {GENERAL_FIELDS.map((f) => (
            <Field key={f.key} field={f} value={settings[f.key]} onChange={change} />
          ))}
        </div>

        <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">Passenger rules</h3>
          {RULE_FIELDS.map((f) => (
            <Field key={f.key} field={f} value={settings[f.key]} onChange={change} />
          ))}
        </div>

        <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white">Fare discount (from promotion budget)</h3>
          <p className="mb-4 text-sm text-gray-500">
            Adjust discount amount and budget from the{" "}
            <Link to="/enhanced-promotions" className="text-blue-600 underline">Enhanced Promotions</Link> screen. These values are read-only here.
            If a promotion is inactive or its budget is used up, the reminder is still sent as a fare-lock only (no discount).
            When a driver accepts a discounted ride, the discount is paid to the driver from the promotion budget so the driver earns on the full fare.
          </p>
          <PromoCard
            title="New passengers"
            promoId={NEW_PROMO_ID}
            promo={promoFor(NEW_PROMO_ID)}
            budget={budgetFor(NEW_PROMO_ID)}
            description="Used for passengers at or below the new-passenger trip limit."
          />
          <PromoCard
            title="Existing passengers"
            promoId={EXISTING_PROMO_ID}
            promo={promoFor(EXISTING_PROMO_ID)}
            budget={budgetFor(EXISTING_PROMO_ID)}
            description="Used for passengers above the new-passenger trip limit. Inactive by default."
          />
        </div>

        {message && <p className="mb-4 text-sm text-gray-700 dark:text-gray-200">{message}</p>}
        <button onClick={save} disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2 text-white disabled:opacity-50">
          {saving ? "Saving..." : "Save settings"}
        </button>
      </div>
    </DashboardLayout>
  );
}
